import { SelectInput} from "components/core";
import { useFormik } from "formik";
import { FC } from "react";
import { wrapClick, classNames } from "utils";
import {
  IResolveFATServiceOrderFormSchema,
  PhysicalInspectionSchema,
  PhysicalInspectionHeaderMap,
} from "./schema";
import lodash from "lodash";

export interface PhysicalInspectionFormProps {
  handleNext: (values: IResolveFATServiceOrderFormSchema["physicalInspection"]) => void;
  handlePrevious: () => void;
  initialValues: IResolveFATServiceOrderFormSchema["physicalInspection"];
  values: IResolveFATServiceOrderFormSchema;
  handleCancel: () => void;
  meterContractor: any;
}

const PhysicalInspectionForm: FC<PhysicalInspectionFormProps> = ({
  initialValues,
  handleNext,
  handleCancel,
  meterContractor,
  handlePrevious,
}) => {

  const form = useFormik({
    initialValues,
    validationSchema: PhysicalInspectionSchema,
    onSubmit: (values) => {
      handleNext(values);
    },
    onReset: () => {
      handleCancel?.();
    },
  });

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="space-y-6 divide-y divide-gray-200 p-6 flex-1 overflow-y-auto">
        <div>
          <span className="text-xs font-light">Physical Inspection Information</span>
          <div className="grid grid-cols-3 gap-6 mt-2">
            <div className="col-span-3">
              <div className="mt-1 w-full ">
                <table className="min-w-full divide-y divide-gray-300 border border-gray-300 rounded-md">
                  <thead className="bg-gray-50">
                    <tr className="divide-x divide-gray-200">
                      <th
                        scope="col"
                        className="whitespace-nowrap px-2 py-2 text-left text-sm font-semibold text-gray-900"
                      >
                        Item
                      </th>
                      <th
                        scope="col"
                        className="whitespace-nowrap px-2 py-2 text-sm font-semibold text-gray-900 w-[100px]"
                      >
                        Status
                      </th>
                      <th
                        scope="col"
                        className="whitespace-nowrap px-2 py-2 text-sm font-semibold text-gray-900 w-[300px]"
                      >
                        Comments
                      </th>

                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                  {lodash.map(
                    Object.keys(PhysicalInspectionHeaderMap),
                    (header, idx) => {
                      const id = `${header}`;

                      return (
                        <tr key={idx} className="divide-x divide-gray-200">
                          <td className="p-0">
                            <input
                              key={idx}
                              type={"text"}
                              id={`index`}
                              name={`index`}
                              value={`${PhysicalInspectionHeaderMap[header as keyof  typeof PhysicalInspectionHeaderMap]}`}
                              placeholder={"eg. 1"}
                              disabled
                              className={classNames(
                                "focus:ring-primary-500 focus:border-primary-500 border-gray-200 bg-gray-50",
                                "block w-full sm:text-sm placeholder:font-light placeholder:text-xs h-[38px] border-0 focus:border"
                              )}
                            />
                          </td>
                          <td className="p-0 whitespace-nowrap w-[100px] text-sm text-gray-500 dark:text-gray-400 text-right w-[200px] h-[38px]">
                            <SelectInput
                              id={`${id}.status`}
                              labelHidden
                              errorsHidden   
                              options={[
                                { label: "--- Select Status ---", value: "" },
                                { label: "Present", value: "Present" },
                                { label: "Absent", value: "Absent" },
                              ]}
                              {...form}
                            />
                          </td>
                          <td className="p-0 relative">
                            <input
                              key={idx}
                              type={"text"}
                              id={`${id}.notes`}
                              name={`${id}.notes`}
                              value={lodash.get(
                                form.values,
                                `${id}.notes`,
                                ""
                              )}
                              onChange={form.handleChange}
                              onBlur={form.handleBlur}
                              placeholder={"eg. Comments..."}
                              className={classNames(
                                lodash.get(form.errors, `${id}.notes`) &&
                                lodash.get(form.touched, `${id}.notes`)
                                  ? "focus:ring-primary-500 focus:border-primary-500 ring-red-500 border-red-600 border"
                                  : "focus:ring-primary-500 focus:border-primary-500 border-gray-200 border-0 focus:border",
                                "outline-none block w-[300px] sm:text-sm placeholder:font-light placeholder:text-gray-700 placeholder:text-xs h-[38px]"
                              )}
                            />
                          </td>
                        </tr>
                      );
                    }
                  )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 sm:py-4 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200">
        <button
          type="button"
          data-invalid={!form.isValid}
          onClick={wrapClick(form.handleSubmit)}
          className="w-full inline-flex justify-center data-invalid:bg-gray-200 data-invalid:border-gray-400 data-invalid:cursor-not-allowed data-invalid:text-gray-600 rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
        >
          Next
        </button>
        <button
          type="button"
          className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-900 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 hover:dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
          onClick={wrapClick(form.resetForm)}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={wrapClick(handlePrevious)}
          className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-900 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 hover:dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
        >
          Previous
        </button>   
      </div>
    </div>
  );
};

export default PhysicalInspectionForm;
